import axios from 'axios'
import { useCallback } from 'react'

import { env } from '../env'
import { useDialogBox } from './useDialogBox'

interface Coordinates {
  latitude: number
  longitude: number
}

export function useGeolocation() {
  const { showDialog } = useDialogBox()

  const geolocationGoogleFallback = useCallback(async (error: GeolocationPositionError) => {
    try {
      const response = await axios.post(`https://www.googleapis.com/geolocation/v1/geolocate?key=${env.VITE_GOOGLE_API_KEY}`,
        {
          considerIp: true,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        })

      if (response.status === 200) {
        return { latitude: response.data.location.lat, longitude: response.data.location.lng }
      } else {
        throw error
      }
    } catch (error) {
      if (error instanceof GeolocationPositionError) {
        const title = 'Erro ao capturar localização!'
        if (error.PERMISSION_DENIED) {
          showDialog({
            title,
            description: `Você precisa permitir que o site acesse sua localização. Caso já tenha recusado,
                acesse as configurações e permissões da página para permitir o acesso à sua localização.`,
          })
        }
        if (error.POSITION_UNAVAILABLE) {
          showDialog({
            title,
            description: `A localização do seu dispositivo pode estar desativada.
                Verifique e tente novamente.`,
          })
        }
        if (error.TIMEOUT) {
          showDialog({
            title,
            description: `O serviço de geolocalização demorou demais para responder.
                Verifique a estabilidade da sua conexão.`,
          })
        }
      } else {
        console.error('Erro no fallback de geolocalização:', error)
        showDialog({
          title: 'Erro inesperado de geolocalização',
          description: 'Erro: ' + error,
        })
      }
    }
  }, [showDialog])

  const getGeolocation = useCallback(async (): Promise<Coordinates | null> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation
          .getCurrentPosition(
            position => {
              const { latitude, longitude } = position.coords
              resolve({ latitude, longitude })
            },
            async (error) => {
              const position = await geolocationGoogleFallback(error)
              if (position) {
                resolve(position)
              } else {
                reject(error)
              }
            },
            {
              timeout: 60000,
              maximumAge: 2 * 60000,
            },
          )
      } else {
        showDialog({
          title: 'Geolocalização não é suportada nesse navegador',
        })
        reject('Geolocation not suported')
      }
    })
  }, [geolocationGoogleFallback, showDialog])

  return getGeolocation
}
